import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LifeBuoy, ArrowLeft, Loader2, Clock, CheckCircle2,
  Search, Filter,
} from 'lucide-react';
import { getUserFacingBookingId } from '../utils/userFacingBookingId';
import {
  makeOptimisticMessage,
  maxMessageCursor,
  mergeThreadMessages,
  useSupportTabTitle,
  useSupportThreadRealtime,
  useThreadScroll,
} from '../utils/supportThreadRealtime';
import {
  B2C_STATUS_TABS,
  SUPPORT_API,
  SUPPORT_CATEGORIES,
  PRIORITY_COLORS,
  getCategoryLabel,
} from '../components/support/supportConstants';
import { SupportStatusBadge } from '../components/support/SupportStatusBadge';
import { BookingSummaryCard } from '../components/support/BookingSummaryCard';
import { PendingPaymentBanner, PaymentsPanel } from '../components/support/PaymentsPanel';
import { SupportMessageThread } from '../components/support/SupportMessageThread';
import { SupportReplyComposer } from '../components/support/SupportReplyComposer';

export { BookingSummaryCard, PendingPaymentBanner, PaymentsPanel } from '../components/support';

const API = SUPPORT_API;

/* ═══════════════════════════════════════════════════════════════════════════
 *  MY TICKETS — B2C only
 * ═══════════════════════════════════════════════════════════════════════════ */
export const MyTickets = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [tab, setTab] = useState('all');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const limit = 25;
  const basePath = '/my-tickets';

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const r = await axios.get(`${API}/support/tickets`, {
        withCredentials: true,
        params: {
          page,
          limit,
          status: tab === 'all' ? 'all' : tab,
          category,
          search: searchDebounced.trim() || undefined,
        },
      });
      setRows(r.data.tickets || []);
      setTotal(r.data.total || 0);
      setCounts(r.data.counts || {});
    } catch (e) {
      setRows([]);
      setTotal(0);
      setCounts({});
      setListError(e.response?.data?.detail || 'Could not load tickets');
    }
    setLoading(false);
  }, [page, tab, category, searchDebounced]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-orange-500" /> My Support Tickets
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Track your requests and conversations with our support team.
          </p>
        </div>
        <div className="text-right text-sm text-gray-500">
          Total: <strong className="text-gray-900">{counts.all ?? 0}</strong>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1 w-fit overflow-x-auto">
        {B2C_STATUS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`mytickets-tab-${t.id}`}
            onClick={() => { setTab(t.id); setPage(1); }}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition whitespace-nowrap ${
              tab === t.id
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === t.id ? 'bg-white/20 text-white' : t.tone}`}>
              {counts[t.id] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          data-testid="mytickets-category"
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
        >
          <option value="all">All Categories</option>
          {Object.entries(SUPPORT_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="mytickets-search"
            type="text"
            placeholder="Search by ref, booking, subject"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-full"
          />
        </div>
      </div>

      {listError && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{listError}</div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            {(counts.all ?? 0) === 0
              ? "You haven't raised any support tickets yet."
              : 'No tickets match your filters.'}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium text-gray-600">Reference</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Booking</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Category</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Subject</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Priority</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Last Update</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`${basePath}/${t.id}`)}
                      data-testid={`mytickets-row-${t.id}`}
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-2 font-mono text-xs text-blue-600">{t.reference}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-600">{getUserFacingBookingId({ booking_id: t.booking_id })}</td>
                      <td className="px-4 py-2">{getCategoryLabel(t.category)}</td>
                      <td className="px-4 py-2 max-w-[260px] truncate">{t.subject || '—'}</td>
                      <td className={`px-4 py-2 capitalize ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2"><SupportStatusBadge status={t.status} variant="b2c" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-gray-100">
              {rows.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate(`${basePath}/${t.id}`)}
                  data-testid={`mytickets-card-${t.id}`}
                  className="block w-full text-left p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-blue-600">{t.reference}</div>
                      <div className="font-semibold text-gray-900 mt-0.5 text-sm">{t.subject || getCategoryLabel(t.category)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {getCategoryLabel(t.category)} · {getUserFacingBookingId({ booking_id: t.booking_id })}
                      </div>
                    </div>
                    <SupportStatusBadge status={t.status} variant="b2c" />
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : '—'}
                  </div>
                </button>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
                <span className="text-gray-500">Page {page} / {totalPages} · {total} tickets</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                          className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                  <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                          className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const MyTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyErr, setReplyErr] = useState('');
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachErr, setAttachErr] = useState('');
  const sinceRef = useRef('');
  const [inboundPing, setInboundPing] = useState(0);
  const { threadRef, onThreadScroll, scrollToBottom, newCount } = useThreadScroll(data?.messages);
  const basePath = '/my-tickets';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await axios.get(`${API}/support/tickets/${id}`, { withCredentials: true });
      setData(r.data);
      sinceRef.current = maxMessageCursor(r.data?.messages || []);
    } catch (e) {
      setData(null);
      setLoadError(e.response?.data?.detail || 'Could not load ticket');
    }
    setLoading(false);
  }, [id]);

  const handleThreadPoll = useCallback((payload) => {
    const incoming = payload?.messages || [];
    if (incoming.some((m) => m.sender_type === 'admin')) {
      setInboundPing((p) => p + 1);
    }
    setData((prev) => {
      if (!prev) return prev;
      const merged = mergeThreadMessages(prev.messages, incoming);
      sinceRef.current = maxMessageCursor(merged);
      const ticket = payload?.ticket ? { ...prev.ticket, ...payload.ticket } : prev.ticket;
      return { ...prev, messages: merged, ticket };
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setInboundPing(0); }, [id]);

  useSupportThreadRealtime({
    ticketId: id,
    streamUrl: id ? `${API}/support/tickets/${id}/stream` : '',
    pollUrl: id ? `${API}/support/tickets/${id}/messages` : '',
    enabled: Boolean(!loading && data?.ticket),
    getSince: () => sinceRef.current,
    onPollResult: handleThreadPoll,
  });

  useSupportTabTitle({
    enabled: Boolean(data?.ticket),
    ticketRef: data?.ticket?.reference,
    ticketStatus: data?.ticket?.status,
    inboundPing,
  });

  const sendReply = async () => {
    const text = reply.trim();
    if (!text) return;
    const senderType = data?.ticket?.created_by_type === 'agent' ? 'agent' : 'b2c';
    const optimistic = makeOptimisticMessage({
      ticketId: id,
      senderType,
      senderName: data?.ticket?.created_by_name || 'You',
      senderRole: senderType,
      message: text,
    });
    setReply('');
    setReplyErr('');
    setData((prev) => (prev ? { ...prev, messages: [...(prev.messages || []), optimistic] } : prev));
    scrollToBottom();
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/support/tickets/${id}/reply`,
        { message: text },
        { withCredentials: true },
      );
      setData((prev) => {
        if (!prev) return prev;
        const without = (prev.messages || []).filter((m) => m.id !== optimistic.id);
        const merged = mergeThreadMessages(without, [r.data.message]);
        sinceRef.current = maxMessageCursor(merged);
        return { ...prev, messages: merged };
      });
    } catch (e) {
      setData((prev) => (
        prev ? { ...prev, messages: (prev.messages || []).filter((m) => m.id !== optimistic.id) } : prev
      ));
      setReply(text);
      setReplyErr(e.response?.data?.detail || 'Reply failed');
    }
    setSending(false);
  };

  const onAttachFiles = async (e) => {
    const fl = Array.from(e.target.files || []);
    e.target.value = '';
    if (!fl.length) return;
    setAttachUploading(true);
    setAttachErr('');
    try {
      const form = new FormData();
      fl.slice(0, 5).forEach((f) => form.append('files', f));
      const r = await axios.post(`${API}/support/tickets/${id}/attach`, form, { withCredentials: true });
      setData((prev) => {
        if (!prev) return prev;
        const merged = mergeThreadMessages(prev.messages, [r.data.message]);
        sinceRef.current = maxMessageCursor(merged);
        return { ...prev, messages: merged };
      });
      scrollToBottom();
    } catch (err) {
      setAttachErr(err.response?.data?.detail || 'Upload failed');
    }
    setAttachUploading(false);
  };

  const pendingPayment = useMemo(() => {
    const list = data?.payments || [];
    return list.find((p) => p.type === 'ADDITIONAL_COLLECTION' && p.status === 'pending') || null;
  }, [data]);

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
  if (loadError) return <div className="p-12 text-center text-red-600 text-sm">{loadError}</div>;
  if (!data?.ticket) return <div className="p-12 text-center text-gray-500">Ticket not found.</div>;

  const t = data.ticket;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button type="button" onClick={() => navigate(basePath)} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <BookingSummaryCard booking={data.booking} snap={t.booking_snapshot} bookingId={t.booking_id} />

      {pendingPayment && (
        <PendingPaymentBanner ticketRef={id} payment={pendingPayment} onDone={load} />
      )}

      <div className="mb-3">
        <PaymentsPanel payments={data.payments || []} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono text-xs text-blue-600">{t.reference}</div>
              <div className="font-bold text-gray-900 mt-0.5">{t.subject || getCategoryLabel(t.category)}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {getCategoryLabel(t.category)} · Booking {getUserFacingBookingId({ booking_id: t.booking_id })}
              </div>
            </div>
            <SupportStatusBadge status={t.status} variant="b2c" />
          </div>

          {t.first_admin_reply_at && (
            <div className="mt-2 text-[11px] text-gray-500">
              First support reply: {new Date(t.first_admin_reply_at).toLocaleString()}
            </div>
          )}

          {t.status === 'closed' && t.refund_amount && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs">
              <strong className="text-emerald-800">Closed</strong> · Refund ₹{t.refund_amount.toLocaleString()}
              {t.refund_reference && <> · Ref {t.refund_reference}</>}
            </div>
          )}
        </div>

        <SupportMessageThread
          messages={data.messages}
          ticketRef={t.id}
          ticket={t}
          theme="b2c"
          threadRef={threadRef}
          onThreadScroll={onThreadScroll}
          newCount={newCount}
          onScrollToBottom={scrollToBottom}
        />

        {t.status !== 'closed' && (
          <SupportReplyComposer
            reply={reply}
            onReplyChange={setReply}
            onSend={sendReply}
            onAttachFiles={onAttachFiles}
            sending={sending}
            attachUploading={attachUploading}
            attachErr={attachErr}
            replyErr={replyErr}
            theme="b2c"
          />
        )}
      </div>
    </div>
  );
};

export default MyTickets;
